import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import authService from '../../services/authService';
import MfaChallenge from './MfaChallenge';
import './Auth.css';
import Toast from '../Toast';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [showMfaChallenge, setShowMfaChallenge] = useState(false);
  const [pendingLogin, setPendingLogin] = useState(null);

  const greetings = [
    'Welcome to my parlor...',
    'How deep does the rabbit hole go?',
    "Come in, we've been expecting you!"
  ];
  const [greeting] = useState(() => {
    const index = Math.floor(Math.random() * greetings.length);
    return greetings[index];
  });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, show: false }));
  };

  useEffect(() => {
    // Check for session expired parameter
    // Only show if user is NOT currently logged in (to avoid showing after successful login)
    const urlParams = new URLSearchParams(location.search);
    const sessionExpired = urlParams.get('sessionExpired');
    if (sessionExpired === 'true') {
      // Check if user exists - if they do, they just logged in, so don't show expired message
      const user = authService.getCurrentUser();
      if (!user) {
        // Set toast with persist flag - toast will stay until user dismisses it
        setToast({ 
          show: true, 
          message: 'Your session has expired. Please login again.', 
          type: 'warning',
          persist: true // Make toast persistent so user must acknowledge
        });
      }
      // Remove the query parameter from URL to prevent showing toast on refresh
      // Use setTimeout to ensure toast state is set before URL change
      setTimeout(() => {
        window.history.replaceState({}, '', '/login');
      }, 100);
    }
  }, [location.search]);

  useEffect(() => {
    const handleGoogleOAuth = async () => {
      // Handle Google OAuth callback - use URLSearchParams directly for immediate access
      const urlParams = new URLSearchParams(location.search);
      const googleToken = urlParams.get('google_token');
      const success = urlParams.get('success');
      const error = urlParams.get('error');

      // Only process if we have parameters to avoid multiple runs
      if (!googleToken && !success && !error) {
        return;
      }

      if (googleToken && success === 'true') {
        const useCookies = urlParams.get('useCookies') === 'true';
        const refreshToken = urlParams.get('refresh_token');
        
        // Store tokens based on storage method
        if (useCookies) {
          // Tokens are in httpOnly cookies, don't store in localStorage
          // Only store user info after fetching
          authService.useCookies = true;
        } else {
          // Store tokens in localStorage
          localStorage.setItem('authToken', googleToken);
          if (refreshToken) {
            localStorage.setItem('refreshToken', refreshToken);
          }
        }
        
        // Fetch and store user information with timeout
        try {
          // Set a timeout for getUserInfo to prevent hanging
          const userInfoPromise = authService.getUserInfo();
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Timeout')), 8000)
          );
          
          const userInfo = await Promise.race([userInfoPromise, timeoutPromise]);
          localStorage.setItem('user', JSON.stringify(userInfo));
          
          // Dispatch event to notify header components of auth state change
          try { window.dispatchEvent(new CustomEvent('authStateChanged')); } catch {}
          
          // Check user preferences for default landing page (same as regular login)
          try {
            const t = authService.getToken && authService.getToken();
            const res = await fetch('/api/userpreferences', { 
              headers: t ? { Authorization: `Bearer ${t}` } : {}, 
              credentials: 'include' 
            });
            if (res.ok) {
              const prefs = await res.json();
              const dest = (prefs?.DefaultGlobalLanding || '/dashboard');
              try { showToast('Signed in', 'success'); } catch {}
              setTimeout(() => navigate(dest, { replace: true }), 500);
              return;
            }
          } catch (prefErr) {
            console.error('Failed to fetch user preferences:', prefErr);
          }
          
          // Fallback to dashboard if preferences fetch fails
          try { showToast('Signed in', 'success'); } catch {}
          setTimeout(() => navigate('/dashboard', { replace: true }), 500);
        } catch (err) {
          console.error('Failed to fetch user info:', err);
          // If getUserInfo fails or times out, still try to navigate (cookies might be set)
          // Store minimal user info from token if possible
          try {
            const tokenParts = googleToken.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              localStorage.setItem('user', JSON.stringify({
                id: payload.nameid,
                email: payload.email,
                firstName: payload.firstName,
                lastName: payload.lastName
              }));
            }
          } catch {}
          
          // Dispatch event to notify header components of auth state change
          try { window.dispatchEvent(new CustomEvent('authStateChanged')); } catch {}
          
          // Try to get user preferences even if getUserInfo failed
          try {
            const t = authService.getToken && authService.getToken();
            const res = await fetch('/api/userpreferences', { 
              headers: t ? { Authorization: `Bearer ${t}` } : {}, 
              credentials: 'include' 
            });
            if (res.ok) {
              const prefs = await res.json();
              const dest = (prefs?.DefaultGlobalLanding || '/dashboard');
              try { showToast('Signed in', 'success'); } catch {}
              navigate(dest, { replace: true });
              return;
            }
          } catch (prefErr) {
            console.error('Failed to fetch user preferences:', prefErr);
          }
          
          // Still navigate to dashboard - don't wait, cookies should work
          try { showToast('Signed in', 'success'); } catch {}
          navigate('/dashboard', { replace: true });
        }
      } else if (error) {
        switch (error) {
          case 'invalid_state':
            showToast('Invalid authentication state. Please try again.', 'error');
            break;
          case 'no_code':
            showToast('No authorization code received. Please try again.', 'error');
            break;
          case 'token_exchange_failed':
            showToast('Failed to exchange authorization code. Please try again.', 'error');
            break;
          case 'user_info_failed':
            showToast('Failed to retrieve user information from Google. Please try again.', 'error');
            break;
          case 'no_email_from_google':
            showToast('No email address received from Google. Please try again.', 'error');
            break;
          case 'user_creation_failed':
            showToast('Failed to create account with Google. Please try again.', 'error');
            break;
          case 'google_auth_error':
            showToast('An error occurred during Google authentication. Please try again.', 'error');
            break;
          default:
            showToast('Authentication failed. Please try again.', 'error');
        }
      }
    };

    handleGoogleOAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]); // Only depend on location.search to avoid multiple runs

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await authService.login(formData);
      
      // Check if MFA is required
      if (result.requiresMfa || result.code === 'mfa_required') {
        setPendingLogin({
          email: formData.email,
          password: formData.password,
          rememberMe: formData.rememberMe,
          tempToken: result.tempToken
        });
        setShowMfaChallenge(true);
        setLoading(false);
        return;
      }
      
      // Normal login success - user is already stored in localStorage by authService.login()
      // Dispatch event immediately, then again after a short delay to ensure components catch it
      try { window.dispatchEvent(new CustomEvent('authStateChanged')); } catch {}
      setTimeout(() => {
        try { window.dispatchEvent(new CustomEvent('authStateChanged')); } catch {}
      }, 100);
      
      try {
        const t = authService.getToken && authService.getToken();
        const res = await fetch('/api/userpreferences', { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials: 'include' });
        if (res.ok) {
          const prefs = await res.json();
          const dest = (prefs?.DefaultGlobalLanding || '/dashboard');
          try { showToast('Signed in', 'success'); } catch {}
          setTimeout(() => navigate(dest), 1500);
          return;
        }
      } catch {}
      try { showToast('Signed in', 'success'); } catch {}
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      showToast(err.message || 'Login failed. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSuccess = async () => {
    try {
      const t = authService.getToken && authService.getToken();
      const res = await fetch('/api/userpreferences', { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials: 'include' });
      if (res.ok) {
        const prefs = await res.json();
        const dest = (prefs?.DefaultGlobalLanding || '/dashboard');
        try { showToast('Signed in', 'success'); } catch {}
        // Dispatch event to notify header components of auth state change
        try { window.dispatchEvent(new CustomEvent('authStateChanged')); } catch {}
        setTimeout(() => navigate(dest), 1500);
        return;
      }
    } catch {}
    try { showToast('Signed in', 'success'); } catch {}
    // Dispatch event to notify header components of auth state change
    try { window.dispatchEvent(new CustomEvent('authStateChanged')); } catch {}
    setTimeout(() => navigate('/dashboard'), 1500);
  };

  const handleMfaError = (error) => {
    showToast(error.message || 'MFA verification failed', 'error');
  };

  const handleGoogleSignIn = () => {
    window.location.href = '/api/auth/google-login';
  };

  if (showMfaChallenge && pendingLogin) {
    return (
      <>
        <div className="login-greeting" style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <h2>{greeting}</h2>
        </div>
        <div className="auth-container">
          <div className="auth-card">
            <MfaChallenge
              email={pendingLogin.email}
              password={pendingLogin.password}
              rememberMe={pendingLogin.rememberMe}
              tempToken={pendingLogin.tempToken}
              onSuccess={handleMfaSuccess}
              onError={handleMfaError}
            />
          </div>
        </div>
        <Toast
          message={toast.message}
          type={toast.type}
          show={toast.show}
          onClose={hideToast}
        />
      </>
    );
  }

  return (
    <>
      <div className="login-greeting" style={{ textAlign: 'center', marginBottom: '1rem' }}>
        <h2>{greeting}</h2>
      </div>
      <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
                      <p>Sign in to your PocketPE account</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {/* Toast-based errors; inline error removed for consistency */}
          
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="form-input"
              placeholder="Enter your email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              className="form-input"
              placeholder="Enter your password"
            />
          </div>

          <div className="form-group-checkbox">
            <input
              type="checkbox"
              id="rememberMe"
              name="rememberMe"
              checked={formData.rememberMe}
              onChange={handleChange}
            />
            <label htmlFor="rememberMe">Remember me</label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="auth-button"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          <div className="auth-divider">
            <span>or</span>
          </div>

          <button
            type="button"
            onClick={handleGoogleSignIn}
            className="google-sign-in-button"
            disabled={loading}
          >
            <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          <div className="auth-links">
            <Link to="/forgot-password" className="auth-link">
              Forgot your password?
            </Link>
          </div>
        </form>

        <div className="auth-footer">
          <p>
            Don't have an account?{' '}
            <Link to="/register" className="auth-link">
              Sign up here
            </Link>
          </p>
        </div>
      </div>
    </div>
      <Toast
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={hideToast}
      />
    </>
  );
};

export default Login; 